import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { listOwnedSubmissions } from '../lib/callables';

export type OwnedPhoto = {
  submissionId: string;
  type: string;
  purchasedAt: Date | null;
  creditsPaid: number;
  lastExportedAt: Date | null;
  exportCount: number;
  labelCount: number;
  labelClassIds: string[];
  category: string;
  subcategory: string;
  itemType: string;
  taskTitle: string;
  taskId: string;
  imageUrl: string;
  videoUrl?: string;
  durationSeconds?: number;
};

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function useOwnedSubmissions(uid?: string | null) {
  const [owned, setOwned] = useState<OwnedPhoto[]>([]);
  const [ownedIds, setOwnedIds] = useState<Set<string>>(new Set());
  const [rawOwnedIds, setRawOwnedIds] = useState<Set<string>>(new Set());
  const [annotatedOwnedIds, setAnnotatedOwnedIds] = useState<Set<string>>(new Set());
  const [rawVideoOwnedIds, setRawVideoOwnedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const apply = (rows: OwnedPhoto[]) => {
      setOwned(rows);
      setOwnedIds(new Set(rows.map((r) => r.submissionId)));
      setRawOwnedIds(new Set(rows.filter((r) => r.type === 'rawPhoto').map((r) => r.submissionId)));
      setAnnotatedOwnedIds(new Set(rows.filter((r) => r.type === 'annotatedPhoto').map((r) => r.submissionId)));
      setRawVideoOwnedIds(new Set(rows.filter((r) => r.type === 'rawVideo').map((r) => r.submissionId)));
    };

    if (!uid) {
      apply([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let seq = 0;
    let firstSnapshot = true;

    setLoading(true);

    // The submissions join is admin-only, so the server does it for us.
    const fetchOwned = async () => {
      const mySeq = ++seq;
      try {
        const result = await listOwnedSubmissions();
        if (cancelled || mySeq !== seq) return;
        const rows: OwnedPhoto[] = (result.items || []).map((it) => ({
          submissionId: it.submissionId,
          type: it.type,
          purchasedAt: toDate(it.purchasedAt),
          creditsPaid: typeof it.creditsPaid === 'number' ? it.creditsPaid : 0,
          lastExportedAt: toDate(it.lastExportedAt),
          exportCount: typeof it.exportCount === 'number' ? it.exportCount : 0,
          labelCount: typeof it.labelCount === 'number' ? it.labelCount : 0,
          labelClassIds: Array.isArray(it.labelClassIds) ? it.labelClassIds : [],
          category: it.category,
          subcategory: it.subcategory,
          itemType: it.itemType,
          taskTitle: it.taskTitle,
          taskId: it.taskId,
          imageUrl: it.imageUrl || '',
          videoUrl: it.videoUrl,
          durationSeconds: it.durationSeconds,
        }));
        apply(rows);
      } catch (error) {
        console.error('Failed to load owned submissions', error);
        if (cancelled || mySeq !== seq) return;
        apply([]);
      } finally {
        if (!cancelled && mySeq === seq) setLoading(false);
      }
    };

    // Load once on mount, so the library works even if the listener is denied.
    fetchOwned();

    // The listener is only a change signal — its data is not used. It refetches
    // through the server so a purchase shows up without a page reload.
    const ref = collection(db, 'clients', uid, 'ownedSubmissions');
    const unsub = onSnapshot(
      ref,
      () => {
        if (firstSnapshot) {
          firstSnapshot = false;
          return;
        }
        fetchOwned();
      },
      (error) => {
        console.warn('ownedSubmissions listener unavailable — library will not auto-refresh', error);
      }
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid]);

  return { owned, ownedIds, rawOwnedIds, annotatedOwnedIds, rawVideoOwnedIds, loading };
}
